/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { CSMasterListItemStatus } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  ROW_DIRTY_CLASS,
  ROW_DISABLED_CLASS,
} from '@gh/common-internal-shared';
import { queryFormMode, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { isEditFormMode } from '@gh/core-ui';
import {
  buildEditableSelectColumn,
  CellParams,
  EditableCellOptionSet,
  GridModel,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { isNotEmpty } from '@gh/core-util';

import { ColDef, GridOptions } from 'ag-grid-community';
import { constant, flow, isEmpty, isNil } from 'lodash';

import {
  getPbLinkRecordId,
  PbLinkCsm,
  PbLinkCsmHeading,
  PbLinkCsmItem,
  PbLinkCsmRecord,
  pbLinkCsmRecordFieldState,
  PbLinkCsmSubheading,
  validatePbLinkCsmRecordField,
} from '../../resources/price-book-link.model';
import { PriceBookLinkCommandService } from './price-book-link-command.service';
import {
  PriceBookLinkCsmState,
  PriceBookLinkState,
  queryPbLinkCsm,
  queryPbLinkCsmHeading,
  queryPbLinkCsmItem,
  queryPbLinkCsmSubheading,
} from './price-book-link.state';

@Injectable()
export class PriceBookLinkCsmGridDefinition extends LayoutBasedGridDefinitionWithModel {

  constructor(private commands: PriceBookLinkCommandService,
              private state: State<PriceBookLinkState>,
              private translations: TranslationService) {
    super();
  }

  get editable(): boolean {
    return isEditFormMode(this.state.project(queryFormMode));
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      editable: this.editable,
      getId: getPbLinkRecordId,
      getCellDef: pbLinkCsmRecordFieldState,
      validate: validatePbLinkCsmRecordField,
      command: (entity, name, value) => this.commands.setRecordField(getPbLinkRecordId(entity), name, value),
      getValue: (field, state) => {
        switch (field) {
          case 'heading':
            return state.value.headingId;
          case 'subheading':
            return state.value.subheadingId;
          case 'item':
            return state.value.itemId;
          default:
            return state.value;
        }
      },
    });
  }

  buildGridOptions(): GridOptions {
    return {
      singleClickEdit: true,
      getRowId: (params) => String(getPbLinkRecordId(params.data)),
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: PbLinkCsmRecord }) => !isNil(data.dirtyType),
        [ROW_DISABLED_CLASS]: ({ data }: { data: PbLinkCsmRecord }) =>
          this.getMaster(getPbLinkRecordId(data)).status === CSMasterListItemStatus.Inactive,
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      heading: buildEditableSelectColumn(
        this.buildHeadingOptionSet(),
        this.model.getCellAccessor('heading'),
        {
          hasAlternativeText: ({ data: { recordId } }: CellParams) => isEmpty(this.getMaster(recordId).headings),
          getAlternativeText: constant(this.translations.get('lbl_no_headings')),
          forceClearable: true,
        }),
      subheading: buildEditableSelectColumn(
        this.buildSubheadingOptionSet(),
        this.model.getCellAccessor('subheading'),
        {
          hasAlternativeText: ({ data: { subheading: { headingId } } }: CellParams) =>
            headingId && isEmpty(this.getHeading(headingId).subheadings),
          getAlternativeText: constant(this.translations.get('lbl_no_subheadings')),
          useEditableAsEnabled: this.editable,
          forceClearable: true,
        }),
      item: buildEditableSelectColumn(
        this.buildItemOptionSet(),
        this.model.getCellAccessor('item'),
        {
          hasAlternativeText: ({ data: { item: { subheadingId } } }: CellParams) =>
            subheadingId && isEmpty(this.getSubheading(subheadingId).items),
          getAlternativeText: constant(this.translations.get('lbl_no_items')),
          useEditableAsEnabled: this.editable,
          forceClearable: true,
        }),
    };
  }

  private buildHeadingOptionSet(): EditableCellOptionSet {
    const getOptions = ({ data: { recordId } }: CellParams) => this.getMaster(recordId).headings;
    return {
      getOptions,
      hasOptions: flow(getOptions, isNotEmpty),
      format: ({ data: { heading: { headingId } } }) => headingId && this.getHeading(headingId).name,
    };
  }

  private buildSubheadingOptionSet(): EditableCellOptionSet {
    const getOptions = ({ data: { subheading: { headingId } } }: CellParams) => this.getHeading(headingId).subheadings;
    return {
      getOptions,
      hasOptions: flow(getOptions, isNotEmpty),
      format: ({ data: { subheading: { subheadingId } } }) => subheadingId && this.getSubheading(subheadingId).name,
    };
  }

  private buildItemOptionSet(): EditableCellOptionSet {
    const getOptions = ({ data: { item: { subheadingId } } }: CellParams) => this.getSubheading(subheadingId).items;
    return {
      getOptions,
      hasOptions: flow(getOptions, isNotEmpty),
      format: ({ data: { item: { itemId } } }) =>
        itemId && this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsmItem(s, itemId)).name,
    };
  }

  private getMaster(id: ID): PbLinkCsm {
    return this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsm(s, id))!;
  }

  private getHeading(headingId: ID): PbLinkCsmHeading {
    return this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsmHeading(s, headingId));
  }

  private getSubheading(subheadingId: ID): PbLinkCsmSubheading {
    return this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsmSubheading(s, subheadingId));
  }

  private getItem(itemId: ID): PbLinkCsmItem {
    return this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsmItem(s, itemId));
  }
}
